import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, withLatestFrom, catchError, exhaustMap } from 'rxjs/operators';
import { LoggerService } from 'src/app/modules/core/services/logger/logger.service';
import { NotificationApiService } from './notification-api.service';
import * as notificationActions from './notifications.actions';
import * as notificationSelectors from './notifications.selectors';

@Injectable()
export class NotificationsEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private loggerService: LoggerService,
    private notificationApiService: NotificationApiService
  ) {
    // const filterLogger = this.loggerService.getLogger('Cop.FilterRuntimeService');
  }

  loadUnreadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationActions.loadUnreadNotifications),
      withLatestFrom(this.store$.select(notificationSelectors.selectUnreadQueryParams)),
      switchMap(([action, queryParams]) => {
        const params = action.params ?? queryParams;
        return this.notificationApiService.getUnreadNotifications(params).pipe(
          exhaustMap(res => {
            return [notificationActions.loadUnreadNotificationsComplete({ data: res })];
          }),
          catchError(err => of(notificationActions.setError({ err })))
        );
      })
    )
  );

  dismissHoverNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationActions.dismissHoverNotification),
      switchMap(action => {
        if (action.markRead) {
          this.notificationApiService.markNotificationRead(action.uId).subscribe();
        }

        return [notificationActions.dismissHoverNotificationComplete({ uId: action.uId, markRead: action.markRead })];
      })
    )
  );

  clearNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationActions.clearNotification),
      withLatestFrom(this.store$.select(notificationSelectors.selectUnreadNotifications),
        this.store$.select(notificationSelectors.selectUnreadQueryParams)),
      switchMap(([action, notifications, queryParams]) => {
        const notification = notifications.find(n => n.uId === action.uId);
        if (!notification) {
          return [];
        }

        if (notification.isTransient) {
          return [notificationActions.clearTransientNotification(action)];
        } else {
          this.notificationApiService.markNotificationRead(notification.uId).subscribe();
        }

        const params = queryParams;
        return this.notificationApiService.getUnreadNotifications(params).pipe(
          switchMap(res => {
            return [notificationActions.loadUnreadNotificationsComplete({ data: res })];
          }),
          catchError(err => of(notificationActions.setError({ err })))
        );
      })
    )
  );

  clearAllNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationActions.clearAllNotifications),
      switchMap((action) => {
        return this.notificationApiService.clearAllNotifications(action.clearPermanent).pipe(
          switchMap(() => [notificationActions.clearAllNotificationsComplete()]),
          catchError(err => of(notificationActions.setError({ err })))
        );
      })
    )
  );
}
